import { PROJECT_UNITS_PER_METRE, ProjectMetrics, metricRackSize } from '../projectMetrics';
import * as THREE from 'three';
import { ISolarTracker } from './ISolarTracker';
import { SolarTrackerPanel } from './types/SolarTrackerPanel';

/** Raster-Layouts pro Panelanzahl: [Spalten (x), Reihen (z)].
 * Das GLB-Gestell ist ein 2x2-Rack — für andere Anzahlen wird es entsprechend
 * mitskaliert, damit Rahmen und Module optisch zusammenpassen. */
const PANEL_LAYOUTS: Record<number, [number, number]> = {
  1: [1, 1], 2: [1, 2], 3: [1, 3], 4: [2, 2], 6: [2, 3], 8: [2, 4],
};

export function getPanelLayout(count: number): [number, number] {
  return PANEL_LAYOUTS[count] ?? PANEL_LAYOUTS[4];
}

export class SolarTracker implements ISolarTracker {
  scene: THREE.Scene;
  material: THREE.Material;
  private templateModel?: THREE.Object3D;
  private modelScale: number;
  private modelFixQ: THREE.Quaternion;

  constructor(
    scene: THREE.Scene,
    material: THREE.Material,
    templateModel?: THREE.Object3D,             // optionales GLB-Template
    modelScale: number = 5,                     // einheitliche Skalierung
    modelFixQ: THREE.Quaternion = new THREE.Quaternion() // optionaler Ausrichtungs-Fix
  ) {
    this.scene = scene;
    this.material = material;
    this.templateModel = templateModel;
    this.modelScale = modelScale;
    this.modelFixQ = modelFixQ;
  }

  create(options: {
    name: string;
    type: SolarTrackerPanel['type'];
    position: [number, number, number];
    rotationDeg?: number;      // initialer Track-Winkel (optional)
    mountingDeg?: number;
    alignment?: number;
    compass: 'nord' | 'ost' | 'süd' | 'west';
    mountingAxis?: 'x' | 'z';  // um welche Kante wird gemountet?
    mountFirst?: boolean;      // Reihenfolge: true = Mount→Align
    mountFlip?: boolean;       // Seite invertieren
    parkOnNoSun?: boolean;     // bei fehlender Sonne parken?
    parkDrop?: number;         // Absenk-Strecke in Y (Default: 3 für limited)
    activeLift?: number;       // Anhebung über Basis bei Sonne (aktive Tracking-Höhe)
    energyDay?: number;
    energyTotal?: number;
    panelCount?: number;       // 1|2|3|4|6|8 — Default 4 (wie das GLB-Rack)
    panelColor?: string;       // Modulfarbe (hex)
    panelEfficiency?: number;  // relativer Wirkungsgrad der Modulfarbe (1 = Standard)
    frameColor?: string | null;// Gestellfarbe; null/undefined = Alu-Original
    metrics?: ProjectMetrics;
    sizeScale?: number;        // Gesamtgröße (Gestell+Module); Builder nutzt 2, Siedlung bleibt 1
    /** 'portrait' (Default): Raster hochkant, z.B. 8 Module = 2 breit x 4 tief.
     * 'landscape': transponiert, z.B. 4 breit x 2 tief — gleiche Kipprichtung,
     * das Gestell wird breiter statt tiefer. */
    panelOrientation?: 'portrait' | 'landscape';
    /** Expliziter Rack-Azimut (Grad). Wenn gesetzt: Pose = Yaw(yawDeg) ⨯ Kippung
     * um die eigene X-Achse — gerades Gestell für jeden Winkel. Ersetzt für
     * Builder-Tracker den Legacy-Alignment-Pfad (SunTracker rechnet identisch). */
    yawDeg?: number | null;
  }): SolarTrackerPanel {
    const {
      name,
      type,
      position,
      rotationDeg = 0,
      mountingDeg = 0,
      alignment = 180,
      compass,
      mountingAxis = 'x',
      mountFirst = false,
      mountFlip = false,
      parkOnNoSun,                           // Default abhängig vom Typ (s.u.)
      parkDrop,                              // Default abhängig vom Typ (s.u.)
      activeLift = 0,
      energyDay = 0,
      energyTotal = 0,
      panelCount = 4,
      panelColor = '#10151c',
      panelEfficiency = 1,
      frameColor = null,
      metrics,
      sizeScale = 1,
      panelOrientation = 'portrait',
      yawDeg = null,
    } = options;


    // Root + Metadaten
    const root = new THREE.Group() as SolarTrackerPanel;
    root.name = name;
    (root as any).type = type;
    (root as any).mountingDeg = mountingDeg;
    (root as any).rotationDeg = rotationDeg;
    (root as any).alignment = alignment;
    (root as any).mountingAxis = mountingAxis;
    (root as any).mountFirst = mountFirst;
    (root as any).mountFlip = mountFlip;
    (root as any).compass = compass;
    (root as any).yawDeg = yawDeg;


    // Park-Defaults: nur limited parkt standardmäßig um 3 Einheiten
    const defaultParkDrop = (type === 'limited' || type === 'single' || type === 'unlimited') ? 5 : 0;
    const defaultParkOnNoSun = (type === 'limited' || type === 'single' || type === 'unlimited');

    (root as any).parkOnNoSun = (parkOnNoSun ?? defaultParkOnNoSun);
    (root as any).parkDrop = (parkDrop ?? defaultParkDrop);
    (root as any).activeLift = activeLift;
    (root as any).isParked = false;

    // Basisposition speichern, damit Absenken/Zurücksetzen sauber funktioniert
    const basePos = new THREE.Vector3(position[0], position[1], position[2]);
    (root as any).basePosition = basePos.clone();

    (root as any).energyDay = energyDay;
    (root as any).energyTotal = energyTotal;
    (root as any).energyHistory = [];

    // Panel-Bestückung & Farben — geht in Optik UND Energieberechnung ein
    (root as any).panelCount = panelCount;
    (root as any).panelEfficiency = panelEfficiency;
    (root as any).panelColor = panelColor;
    (root as any).frameColor = frameColor;
    (root as any).sizeScale = sizeScale;
    (root as any).panelOrientation = panelOrientation;

    root.position.copy(basePos);
    root.castShadow = true;
    root.receiveShadow = true;

    // Modell oder Fallback-Mesh
    let [cols, rows] = getPanelLayout(panelCount);
    if (panelOrientation === 'landscape') [cols, rows] = [rows, cols];
    const metricSize=metrics?metricRackSize(cols,rows,panelOrientation==='landscape',metrics):null;
    if (this.templateModel) {
      const model = this.templateModel.clone(true);
      model.traverse((n: any) => {
        if (n.isMesh) { n.castShadow = true; n.receiveShadow = true; }
      });
      // Gestell passend zur Modulanzahl strecken/stauchen (GLB-Rack ist 2x2):
      // x = Spaltenrichtung, z = Reihenrichtung (Schienen laufen entlang z).
      model.scale.set(
        this.modelScale * (cols / 2) * sizeScale,
        this.modelScale * sizeScale,
        this.modelScale * (rows / 2) * sizeScale
      );
      model.applyQuaternion(this.modelFixQ);
      if(metricSize){
        model.updateWorldMatrix(true,true);
        const b=new THREE.Box3().setFromObject(model),size=b.getSize(new THREE.Vector3());
        const wrapper=new THREE.Group();wrapper.add(model);
        wrapper.scale.set(metricSize.width/size.x,metricSize.height/size.y,metricSize.depth/size.z);
        // Bake the normalized frame beneath a group in the rack's local coordinates.
        wrapper.updateWorldMatrix(true,true);
        const bounds=new THREE.Box3().setFromObject(wrapper),center=bounds.getCenter(new THREE.Vector3());
        wrapper.position.set(-center.x,-bounds.min.y,-center.z);
        if(frameColor)tintFrame(wrapper,frameColor);
        const plates=buildPanelPlates(wrapper,cols,rows,panelColor,metricSize);
        root.add(wrapper,plates);
      } else {
      if (frameColor) tintFrame(model, frameColor);
      // Platten VOR dem Anhängen bauen: die Bbox wird dann garantiert im
      // Root-lokalen Raum berechnet (Root ist bereits auf basePos verschoben).
      const plates = buildPanelPlates(model, cols, rows, panelColor);
      root.add(model);
      root.add(plates);
      }
    } else {
      const geometry = new THREE.BoxGeometry(metricSize?.width ?? 10 * (cols / 2) * sizeScale, metricSize?.height ?? 0.5, metricSize?.depth ?? 15 * (rows / 2) * sizeScale);
      const mesh = new THREE.Mesh(geometry, this.material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      root.add(mesh);
    }

    // --- Initialrotation (ohne Tracking) ---
    // Expliziter Yaw-Pfad (Builder): identische Formel wie SunTracker.rotatePanel,
    // damit die Pose vom ersten Frame an stimmt und der erste Sonnen-Tick nichts
    // "umspringen" lässt.
    if (yawDeg != null) {
      const q = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), THREE.MathUtils.degToRad(yawDeg))
        .multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), THREE.MathUtils.degToRad(mountingDeg)));
      root.setRotationFromQuaternion(q);
      this.scene.add(root);
      return root;
    }

    // --- Legacy: mit wählbarer Reihenfolge (kalibrierte Siedlungs-Configs) ---
    const alignQ = new THREE.Quaternion().setFromAxisAngle(
      new THREE.Vector3(0, 1, 0), THREE.MathUtils.degToRad(alignment)
    );

    // lokale Kippachse wählen
    const axisLocal =
      (mountingAxis === 'z') ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(1, 0, 0);

    // optional Seite invertieren
    const axisWithSide = ((root as any).mountFlip ? axisLocal.clone().negate() : axisLocal.clone());

    // Reihenfolge beachten: wenn Align zuerst, dann Achse mit drehen
    const axisForMount = mountFirst ? axisWithSide : axisWithSide.applyQuaternion(alignQ);

    const mountQ = new THREE.Quaternion().setFromAxisAngle(
      axisForMount, THREE.MathUtils.degToRad(mountingDeg)
    );

    // optionaler initialer Track (wird später von SunTracker überschrieben)
    const trackQ = new THREE.Quaternion().setFromAxisAngle(
      new THREE.Vector3(1, 0, 0), THREE.MathUtils.degToRad(rotationDeg)
    );

    const combinedQ = mountFirst
      ? mountQ.clone().multiply(alignQ).multiply(trackQ)   // Mount → Align → Track
      : alignQ.clone().multiply(mountQ).multiply(trackQ);  // Align → Mount → Track

    root.setRotationFromQuaternion(combinedQ);

    this.scene.add(root);
    return root;
  }
/*
  createColumn(options: {
    position: [number, number, number];
    height?: number;
    color?: string;
  }): THREE.Mesh {
    const { position, height = 10, color = '#cccc00' } = options;
    const geometry = new THREE.CylinderGeometry(0.5, 0.5, height, 32);
    const material = new THREE.MeshLambertMaterial({ color });
    const column = new THREE.Mesh(geometry, material);
    column.position.set(...position);
    column.castShadow = true;
    column.receiveShadow = true;
    this.scene.add(column);
    return column;
  }*/
}

/** Färbt das Alu-Gestell um. Die GLB-Materialien sind zwischen allen geklonten
 * Trackern geteilt — deshalb pro Mesh klonen, sonst färben sich alle Tracker mit. */
function tintFrame(model: THREE.Object3D, hex: string) {
  const cloned = new Map<THREE.Material, THREE.Material>();
  model.traverse((n: any) => {
    if (!n.isMesh || !n.material) return;
    const apply = (m: any) => {
      if (!cloned.has(m)) {
        const c = m.clone();
        if (c.color) c.color.set(hex);
        cloned.set(m, c);
      }
      return cloned.get(m)!;
    };
    n.material = Array.isArray(n.material) ? n.material.map(apply) : apply(n.material);
  });
}

/** Erzeugt die PV-Modul-Platten als cols x rows Raster über der Gestellfläche.
 * Liegt im Root-Koordinatensystem in der XZ-Ebene → die Flächennormale ist die
 * lokale +Y-Achse, exakt die Normale, mit der EnergyTracker rechnet, und die
 * Platten drehen beim Tracking automatisch mit. */
function buildPanelPlates(scaledModel: THREE.Object3D, cols: number, rows: number, colorHex: string, metricSize: ReturnType<typeof metricRackSize>|null = null): THREE.Group {
  const group = new THREE.Group();
  group.name = 'pv_panels';

  scaledModel.updateWorldMatrix(false, true);
  const bbox = new THREE.Box3().setFromObject(scaledModel);
  if (!Number.isFinite(bbox.min.x)) return group;

  const w = bbox.max.x - bbox.min.x;
  const d = bbox.max.z - bbox.min.z;
  const cellW = w / cols;
  const cellD = d / rows;
  const GAP = 0.94; // kleine Fuge zwischen den Modulen
  // Dicke/Abstand proportional zur Modulgröße, damit größere Tracker nicht papierdünn wirken
  const thickness = metricSize ? 0.04*PROJECT_UNITS_PER_METRE : Math.max(0.18, cellD * 0.03);
  const y = bbox.max.y + thickness * (metricSize ? 0.5 : 0.7);

  const mat = new THREE.MeshStandardMaterial({
    color: new THREE.Color(colorHex),
    roughness: 0.35,
    metalness: 0.25,
  });
  const plateGeo = new THREE.BoxGeometry(metricSize?.moduleWidth ?? cellW * GAP, thickness, metricSize?.moduleDepth ?? cellD * GAP);
  const edgeGeo = new THREE.EdgesGeometry(plateGeo);
  const edgeMat = new THREE.LineBasicMaterial({ color: 0x33383f });

  for (let cx = 0; cx < cols; cx++) {
    for (let rz = 0; rz < rows; rz++) {
      const plate = new THREE.Mesh(plateGeo, mat);
      plate.castShadow = true;
      plate.receiveShadow = true;
      plate.position.set(
        bbox.min.x + (metricSize ? metricSize.moduleWidth/2+cx*(metricSize.moduleWidth+metricSize.gap) : (cx+0.5)*cellW),
        y,
        bbox.min.z + (metricSize ? metricSize.moduleDepth/2+rz*(metricSize.moduleDepth+metricSize.gap) : (rz+0.5)*cellD)
      );
      plate.add(new THREE.LineSegments(edgeGeo, edgeMat));
      group.add(plate);
    }
  }
  return group;
}
